import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

export default new Vuex.Store({
    state: {
        loginState: {
            isLoggedIn: false,
            isPermissionDisabled: null,
            email: undefined,
            userId: undefined,
            orgId: undefined
        },
        schema: [],
        dataset: {
            filename: null,
            showFileUploadSuccess: false,
            headers: null,
            contents: null,
            parsedData: null,
            datasetID: null,
            datasetLabel: '',
            datasetDescription: '',
            organizationIds: ''
        },
        datasetToEdit: {
            datasetLabel: null,
            datasetDescription: null,
            organizationIds: null,
            datasetID: null
        },
        datasetReadOnly: {
            datasetLabel: null,
            columnFormat: null
        },
        papaParseConfig: {
            delimiter: '', // auto-detect
            newline: '', // auto-detect
            quoteChar: '"',
            escapeChar: '"',
            header: true,
            trimHeaders: true,
            dynamicTyping: true,
            preview: 10,
            encoding: '',
            worker: false,
            comments: false,
            step: undefined,
            complete: undefined,
            error: undefined,
            download: false,
            skipEmptyLines: true,
            chunk: undefined,
            fastMode: undefined,
            beforeFirstChunk: undefined,
            withCredentials: undefined,
            transform: undefined
        },
        spatialReference: {
            name: {
                label: 'Name',
                index: null,
                dataType: 'string',
                format: 'string'
            },
            latitude: {
                label: 'Latitude',
                index: null,
                dataType: 'decimal',
                format: 'decimal'
            },
            longitude: {
                label: 'Longitude',
                index: null,
                dataType: 'decimal',
                format: 'decimal'
            },
            street: {
                label: 'Street',
                index: null,
                dataType: 'string',
                format: 'string'
            },
            city: {
                label: 'City',
                index: null,
                dataType: 'string',
                format: 'string'
            },
            state: {
                label: 'State',
                index: null,
                dataType: 'string',
                format: 'string'
            },
            country: {
                label: 'Country',
                index: null,
                dataType: 'string',
                format: 'string'
            },
            zip: {
                label: 'Zip Code',
                index: null,
                dataType: 'string',
                format: 'string'
            }
        },
        spatialReferenceSelectionDone: false,
        spatialReferencePickerVisible: false,
        showCreateModal: false,
        showReadOnlyModal: false,
        showEditModal: false,
        showDeleteModal: false,
        isLoading: false,
        isUploading: false,
        lockButtons: false,
        refreshSpatialAssignments: false
    },
    mutations: {
        showSpatialReferencePicker(state, show) {
            if (show) {
                state.spatialReferencePickerVisible = show;
            } else {
                state.spatialReferencePickerVisible = show;
            }
        },
        markSpatialReferenceAsDone(state, done) {
            if (done) {
                state.spatialReferenceSelectionDone = done;
            } else {
                state.spatialReferenceSelectionDone = done;
            }
        },
        addToSchema(state, element) {
            if (element) {
                Vue.set(state.schema, element.index, element);
            }
        },
        updateSchemaElement(state, changedElement) {
            if (state.schema.length > 0 && changedElement.label) {
                const changedSpatialValue = changedElement.label.split(' ')[0].toLowerCase(); // Split in place for "Zip Code" -> "zip" (consider changing);

                // Function to reset schema format.
                const setSpatialIndex = (ref, spatial, format) => {
                    Vue.set(state.schema[ref], 'spatial', spatial);
                    Vue.set(state.schema[ref], 'format', format);
                };

                if (changedElement.index != null) {
                    // Create a map of index to count.
                    const allSpatialIndexes = Object.keys(state.spatialReference).map((ref) => state.spatialReference[ref].index);
                    const countOfIndexes = allSpatialIndexes.reduce((countMap, occurrence) => { countMap[occurrence] = ++countMap[occurrence] || 1; return countMap; }, {});

                    // We gotta check if the index was already there.
                    if (countOfIndexes[changedElement.index] > 1) {
                        Object.keys(state.spatialReference).forEach((spatialRef) => {
                            const oldSpatialValue = state.spatialReference[spatialRef].label.split(' ')[0].toLowerCase();

                            // What other spatialvalues have this same index?
                            if (state.spatialReference[spatialRef].index === changedElement.index && state.spatialReference[spatialRef].label !== changedElement.label) {
                                setSpatialIndex(state.spatialReference[spatialRef].index, undefined, 'string');
                                setSpatialIndex(changedElement.index, changedSpatialValue, changedElement.format);
                                state.spatialReference[oldSpatialValue].index = null;
                            }
                        });
                    } else {
                        // Find if any column already has this same spatial property.
                        const result = state.schema.find((column) => column.spatial === changedSpatialValue);
                        if (result && result.index != null) {
                            // Remove the old spatial index.
                            setSpatialIndex(result.index, undefined, 'string');
                        }
                        // Set the new spatial index.
                        setSpatialIndex(changedElement.index, changedSpatialValue, changedElement.format);
                    }
                } else {
                    // Find if any column already has this same spatial property.
                    const result = state.schema.find((column) => column.spatial === changedSpatialValue);
                    setSpatialIndex(result.index, undefined, 'string');
                    state.spatialReference[changedSpatialValue].index = null;
                }
            }
        },
        resetData(state, removeDescriptions) {
            // If all is true remove dataset descriptive properties.
            if (removeDescriptions) {
                state.dataset.datasetLabel = '';
                state.dataset.datasetDescription = '';
                state.dataset.organizationIds = '';
            }
            // Reset state for uploaded file.
            state.dataset.datasetID = null;
            state.dataset.filename = null;
            state.dataset.showFileUploadSuccess = false;
            state.spatialReferencePickerVisible = false;
            state.spatialReferenceSelectionDone = false;
            state.dataset.parsedData = null;
            state.dataset.headers = null;
            state.dataset.contents = null;
            state.schema = [];
        },
        resetSpatialValuesIndex(state) {
            Object.keys(state.spatialReference).forEach((key) => {
                if (key === 'longitude' || key === 'latitude') {
                    state.spatialReference[key].dataType = 'decimal';
                    state.spatialReference[key].format = 'decimal';
                } else {
                    state.spatialReference[key].dataType = 'string';
                    state.spatialReference[key].format = 'string';
                }
                state.spatialReference[key].index = null;
            });
        },
        setToast(state, update) {
            state.toast.message = update.message;
            state.toast.state = update.state;
        },
        toggleCreateModal(state) {
            state.showCreateModal = !state.showCreateModal;
        },
        toggleReadOnlyModal(state) {
            state.showReadOnlyModal = !state.showReadOnlyModal;
        },
        toggleEditModal(state) {
            state.showEditModal = !state.showEditModal;
        },
        toggleDeleteModal(state) {
            state.showDeleteModal = !state.showDeleteModal;
        },
        toggleSpinner(state, toggleState) {
            if (toggleState === 'undefined') {
                state.isLoading = !state.isLoading;
            } else {
                state.isLoading = toggleState;
            }
        },
        toggleUploadSpinner(state) {
            state.isUploading = !state.isUploading;
        },
        toggleLockButtons(state) {
            state.lockButtons = !state.lockButtons;
        }
    },
    actions: {
    }
});
